import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  useChatAssets,
  useDeleteAsset,
  useRateAssetForChat,
  useUpdateAssetDescriptionForChat,
} from '../hooks/useApi';
import { useChatUpload } from '../hooks/useChatUpload';
import { ConfirmDialog } from './ConfirmDialog';
import { PanoramaViewerModal, canViewAs360 } from './PanoramaViewerModal';
import { StarRating } from './StarRating';
import { RatingReasonChips } from './RatingReasonChips';
import { useComposerBridge } from '../stores/composerBridge';
import { LibraryModal } from './LibraryModal';
import { BASE } from '../api/client';
import type { Asset } from '../api/types';

interface ReferenceTrayProps {
  chatId: string;
  instanceId?: string;
}

/**
 * Panel lateral con todos los assets del project del chat.
 * - GET /chats/:id/assets carga la lista (refetch automático cuando se sube algo desde el chat).
 * - Drop zone propio para subir desde acá también.
 * - Click en una imagen: abre preview ampliado.
 */
export function ReferenceTray({ chatId, instanceId }: ReferenceTrayProps) {
  const { data: assets, isLoading } = useChatAssets(chatId);
  const upload = useChatUpload(chatId);
  const deleteAsset = useDeleteAsset(chatId);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<Asset | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [confirmDelete, setConfirmDelete] = useState<Asset | null>(null);
  const askDelete = (asset: Asset) => setConfirmDelete(asset);
  const performDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    await deleteAsset.mutateAsync(id);
    if (preview?.id === id) setPreview(null);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) void upload.addFiles(files);
  };
  const onFilePicker = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) void upload.addFiles(files);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col h-full p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase text-slate-500 tracking-wide">
          Referencias {assets && `(${assets.length})`}
        </div>
        <div className="flex items-center gap-1">
          {instanceId && (
            <button
              onClick={() => setLibraryOpen(true)}
              className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
              title="Importar de la biblioteca cross-proyecto"
            >
              📚 Biblioteca
            </button>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
            title="Subir desde archivo"
          >
            + Subir
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*"
          onChange={onFilePicker}
          className="hidden"
        />
      </div>

      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex-1 rounded-lg border-2 border-dashed p-2 overflow-y-auto transition-colors ${
          dragOver
            ? 'border-vision bg-vision-dark/20'
            : 'border-slate-700/50 bg-slate-900/30'
        }`}
      >
        {isLoading && (
          <div className="text-xs text-slate-500 text-center py-4">Cargando…</div>
        )}

        {/* Adjuntos en proceso de subida (mientras Tanstack todavía no refetcheó) */}
        {upload.attachments.filter((a) => a.status !== 'done').map((a) => (
          <div
            key={a.id}
            className="mb-2 p-2 bg-slate-800/40 border border-slate-700 rounded text-xs flex items-start gap-2"
          >
            <div className="w-10 h-10 bg-slate-950 rounded overflow-hidden flex-shrink-0">
              {a.mimeType.startsWith('image/') && (
                <img src={a.previewUrl} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-300 truncate">{a.name}</div>
              <div className="text-slate-500">
                {a.status === 'uploading' && 'Subiendo…'}
                {a.status === 'error' && (
                  <span className="text-red-400 block whitespace-normal break-words">
                    ⚠ {friendlyUploadError(a.error)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Lista real desde DB — 2 grupos: lo que subió el operador vs lo que
            produjo la app. Las generadas no-IA (kind 'intermediate': loops,
            frames, videos optimizados) van con las generadas, NO con las subidas. */}
        {assets && assets.length > 0 && (() => {
          const uploads = assets.filter((a) => a.kind === 'user_upload' || a.kind === 'reference');
          const outputs = assets.filter((a) => a.kind !== 'user_upload' && a.kind !== 'reference');
          return (
            <div className="space-y-3">
              {uploads.length > 0 && (
                <AssetGroup
                  title="📎 Subidas"
                  count={uploads.length}
                  assets={uploads}
                  onPick={setPreview}
                  onDelete={askDelete}
                />
              )}
              {outputs.length > 0 && (
                <AssetGroup
                  title="✨ Generadas por la IA"
                  count={outputs.length}
                  accent="vision"
                  assets={outputs}
                  onPick={setPreview}
                  onDelete={askDelete}
                />
              )}
            </div>
          );
        })()}

        {!isLoading && (!assets || assets.length === 0) && upload.attachments.length === 0 && (
          <div className="text-xs text-slate-500 text-center py-6">
            Arrastrá imágenes acá o pegalas en el chat con Cmd/Ctrl+V.
          </div>
        )}
      </div>

      {preview && (
        <PreviewModal
          asset={preview}
          chatId={chatId}
          onClose={() => setPreview(null)}
          onDelete={() => askDelete(preview)}
          onDescriptionUpdated={(updated) => setPreview(updated)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Borrar referencia"
        message={
          confirmDelete
            ? `Vas a borrar ${
                confirmDelete.metadata?.description
                  ? `"${confirmDelete.metadata.description}"`
                  : `#${confirmDelete.metadata?.shortRef ?? 'esta referencia'}`
              } del proyecto. Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Borrar"
        variant="danger"
        onConfirm={() => void performDelete()}
        onCancel={() => setConfirmDelete(null)}
      />

      {libraryOpen && instanceId && (
        <LibraryModal
          chatId={chatId}
          instanceId={instanceId}
          onClose={() => setLibraryOpen(false)}
        />
      )}
    </div>
  );
}

function PreviewModal({
  asset,
  chatId,
  onClose,
  onDelete,
  onDescriptionUpdated,
}: {
  asset: Asset;
  chatId: string;
  onClose: () => void;
  onDelete: () => void;
  onDescriptionUpdated: (updated: Asset) => void;
}) {
  const update = useUpdateAssetDescriptionForChat(chatId);
  const rate = useRateAssetForChat(chatId);
  const [descDraft, setDescDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [panoramaOpen, setPanoramaOpen] = useState(false);
  const [showChips, setShowChips] = useState(false);
  const [sendingCorrection, setSendingCorrection] = useState(false);
  const requestSend = useComposerBridge((s) => s.requestSend);

  const setRating = async (n: number) => {
    const updated = await rate.mutateAsync({ assetId: asset.id, userRating: n });
    onDescriptionUpdated(updated);
    if (n <= 2) setShowChips(true); // calificación baja → pedimos el motivo
  };

  // Gate anti-repetición: guarda el motivo (chips + comentario) en el asset y
  // manda la corrección al chat via el puente, para que el agente regenere
  // corrigiendo ESO y manteniendo el resto igual (no re-tirar a ciegas).
  const confirmCorrection = async (tags: string[], comment: string) => {
    setSendingCorrection(true);
    try {
      const updated = await rate.mutateAsync({
        assetId: asset.id,
        userRatingReason: comment || undefined,
        userRatingTags: tags,
      });
      onDescriptionUpdated(updated);
      const ref = asset.metadata?.shortRef ?? asset.id.slice(0, 8);
      const detail = [tags.length ? `corregí: ${tags.join(', ').toLowerCase()}` : '', comment]
        .filter(Boolean)
        .join('. ');
      const text = `Iterá #${ref}${detail ? ` — ${detail}` : ''}. Mantené TODO lo demás exactamente igual (personaje, ropa, encuadre, estilo).`;
      requestSend({ chatId, text, attachmentIds: [asset.id] });
      setShowChips(false);
      onClose();
    } finally {
      setSendingCorrection(false);
    }
  };

  useEffect(() => {
    setDescDraft(asset.metadata?.description ?? '');
  }, [asset]);

  const save = async () => {
    const trimmed = descDraft.trim();
    if (trimmed === (asset.metadata?.description ?? '')) return;
    setSaving(true);
    try {
      await update.mutateAsync({ assetId: asset.id, description: trimmed });
      onDescriptionUpdated({
        ...asset,
        metadata: { ...(asset.metadata ?? {}), description: trimmed },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-7xl max-h-[95vh] bg-slate-900 rounded-xl overflow-hidden border border-slate-700 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {asset.mimeType.startsWith('image/') ? (
          <img
            src={asset.accessUrl || asset.falCdnUrl || ''}
            alt=""
            // object-cover + w-full ocupa el ancho completo sin barras negras.
            // Para 2:1 fija altura proporcional; para otros ratios recorta lo justo.
            className="w-full h-auto max-h-[60vh] object-cover bg-black"
            style={
              asset.width && asset.height
                ? { aspectRatio: `${asset.width} / ${asset.height}` }
                : undefined
            }
          />
        ) : asset.mimeType.startsWith('video/') ? (
          <video
            src={asset.accessUrl || asset.falCdnUrl || ''}
            controls
            playsInline
            className="w-full max-h-[60vh] bg-black"
            style={
              asset.width && asset.height
                ? { aspectRatio: `${asset.width} / ${asset.height}` }
                : undefined
            }
          />
        ) : asset.mimeType.startsWith('audio/') ? (
          <div className="p-8 bg-slate-950 flex items-center justify-center">
            <audio
              src={asset.accessUrl || asset.falCdnUrl || ''}
              controls
              className="w-full max-w-2xl"
            />
          </div>
        ) : (
          <div className="p-8 text-slate-300">
            Preview no disponible para {asset.mimeType}
          </div>
        )}
        <div className="p-5 border-t border-slate-700 space-y-4 overflow-y-auto">
          {asset.kind === 'generation_output' && (
            <div className="space-y-2 pb-2 border-b border-slate-800">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  Tu rating
                </div>
                <StarRating
                  value={asset.userRating}
                  onChange={(n) => void setRating(n)}
                  size="lg"
                />
              </div>
              {asset.aiRating ? (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    AI rating
                  </div>
                  <div className="flex items-center gap-2">
                    <StarRating value={asset.aiRating} readOnly color="vision" size="lg" />
                    {asset.aiRatingReason && (
                      <span className="text-xs text-slate-400 italic max-w-md">
                        "{asset.aiRatingReason}"
                      </span>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            {!showChips ? (
              <button
                type="button"
                onClick={() => setShowChips(true)}
                className="text-xs text-slate-400 hover:text-vision"
              >
                ↻ Corregir / iterar este resultado
              </button>
            ) : (
              <RatingReasonChips
                kind={asset.kind}
                submitting={sendingCorrection}
                onConfirm={(tags, comment) => void confirmCorrection(tags, comment)}
                onCancel={() => setShowChips(false)}
              />
            )}
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 block">
              Descripción · la lee el agente cada vez que usa esta imagen
            </label>
            <textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              placeholder="Ej: Vista frontal del podcast con la mesa redonda al centro"
              rows={6}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-sm text-slate-100 focus:outline-none focus:border-vision resize-none"
            />
          </div>
          <div className="flex items-center justify-between gap-2 text-xs flex-wrap">
            <div className="text-slate-400 font-mono">
              #{asset.metadata?.shortRef ?? '?'} · {asset.kind} · {formatSize(asset.sizeBytes)}
              {asset.width && asset.height && ` · ${asset.width}×${asset.height}`}
            </div>
            <div className="flex items-center gap-2">
              {canViewAs360(asset) && (
                <button
                  onClick={() => setPanoramaOpen(true)}
                  className="px-3 py-1.5 bg-vision-dark hover:bg-vision rounded text-white"
                  title="Abrir como panorama 360 inmersivo"
                >
                  🌐 Ver en 360°
                </button>
              )}
              <a
                href={`${BASE}/assets/${asset.id}/download`}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-slate-200"
                download
              >
                ⬇ Descargar original
              </a>
              <button
                onClick={() => void save()}
                disabled={saving || descDraft.trim() === (asset.metadata?.description ?? '')}
                className="px-3 py-1.5 bg-vision-dark hover:bg-vision rounded text-white disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar descripción'}
              </button>
              <button
                onClick={onDelete}
                className="px-3 py-1.5 bg-red-900/50 hover:bg-red-700 text-red-200 rounded"
              >
                🗑 Borrar
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-slate-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      {panoramaOpen && (
        <PanoramaViewerModal
          assetId={asset.id}
          caption={asset.metadata?.description ?? `#${asset.metadata?.shortRef ?? ''}`}
          onClose={() => setPanoramaOpen(false)}
        />
      )}
    </div>
  );
}

function AssetGroup({
  title,
  count,
  accent,
  assets,
  onPick,
  onDelete,
}: {
  title: string;
  count: number;
  accent?: 'vision';
  assets: Asset[];
  onPick: (a: Asset) => void;
  onDelete: (a: Asset) => void;
}) {
  const headerColor = accent === 'vision' ? 'text-vision' : 'text-slate-400';
  return (
    <div>
      <div className={`flex items-center gap-2 mb-1.5 px-0.5 text-[10px] uppercase tracking-wider ${headerColor}`}>
        <span className="font-semibold">{title}</span>
        <span className="text-slate-600">({count})</span>
        <div className="flex-1 h-px bg-slate-800" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {assets.map((a) => (
          <div
            key={a.id}
            className="group relative bg-slate-950 rounded-lg overflow-hidden border border-slate-700 hover:border-vision transition-colors"
            title={`${a.kind} · ${formatSize(a.sizeBytes)}`}
          >
            <button onClick={() => onPick(a)} className="block w-full text-left">
              <div className="relative">
                {a.mimeType.startsWith('image/') ? (
                  <img
                    src={a.thumbnailUrl || a.accessUrl || a.falCdnUrl || ''}
                    alt=""
                    loading="lazy"
                    className="w-full h-24 object-cover"
                  />
                ) : (
                  <div className="w-full h-24 flex items-center justify-center text-2xl text-slate-400">
                    {a.mimeType.startsWith('video/') ? '🎬' : a.mimeType.startsWith('audio/') ? '🎵' : '📄'}
                  </div>
                )}
                {a.kind === 'generation_output' && (a.userRating || a.aiRating) && (
                  <div className="absolute bottom-1 right-1 flex items-center gap-1 bg-slate-950/90 px-1.5 py-0.5 rounded text-[10px]">
                    {a.userRating ? (
                      <span className="text-amber-400" title={`Tu rating: ${a.userRating}/5`}>
                        ★{a.userRating}
                      </span>
                    ) : null}
                    {a.aiRating ? (
                      <span className="text-vision" title={`AI rating: ${a.aiRating}/5${a.aiRatingReason ? ` — ${a.aiRatingReason}` : ''}`}>
                        ✨{a.aiRating}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="px-1.5 py-1 text-[10px] bg-slate-950/80 space-y-0.5">
                {a.metadata?.shortRef && (
                  <div className="font-mono text-vision">#{a.metadata.shortRef}</div>
                )}
                {a.metadata?.description ? (
                  <div className="line-clamp-2 leading-tight text-slate-200" title={a.metadata.description}>
                    {a.metadata.description}
                  </div>
                ) : (
                  <div className="text-slate-500 italic">(sin descripción)</div>
                )}
              </div>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(a);
              }}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-950/80 hover:bg-red-700 text-slate-200 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title="Borrar referencia"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * El backend (NestJS) devuelve `{statusCode, message, error}` en JSON. El
 * navegador móvil corta la subida con `Request aborted` cuando la red cae.
 * Convertimos a un mensaje legible — sin esto la UI mostraba JSON crudo
 * truncado a 40 chars y nadie podía entender qué pasó.
 */
function friendlyUploadError(err: string | undefined): string {
  if (!err) return 'Falló la subida';
  if (/aborted/i.test(err)) {
    return 'Subida cortada — la red móvil cortó. Probá de nuevo o pasá a WiFi.';
  }
  if (/413|too large|payload/i.test(err)) {
    return 'Foto demasiado grande (>50 MB). Achicala antes de subir.';
  }
  if (/timeout/i.test(err)) return 'Subida tardó demasiado. Probá de nuevo.';
  try {
    const parsed = JSON.parse(err);
    if (parsed && typeof parsed === 'object' && 'message' in parsed) {
      return String((parsed as { message: unknown }).message);
    }
  } catch {
    // no era JSON, devolvemos el texto crudo
  }
  return err.slice(0, 200);
}
